import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getUserById, updateUser, getAgents } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const EditUser = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [loadingAgents, setLoadingAgents] = useState(true);
    const [validated, setValidated] = useState(false);
    const [error, setError] = useState('');
    const [agents, setAgents] = useState([]);
    const [userAgentId, setUserAgentId] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        gender: '',
        mobileNumber: '',
        status: 'active',
        activeAgentId: ''
    });

    useEffect(() => {
        const loadData = async () => {
            await fetchAgents();
            await fetchUserDetails();
        };
        loadData();
    }, [id]);

    // Update activeAgentId when agents are loaded and userAgentId is available
    useEffect(() => {
        if (userAgentId && agents.length > 0) {
            // The user's agentId is actually the agent's _id, not the agentId field
            // Find matching agent by _id (user.agentId matches agent._id)
            const matchingAgent = agents.find(agent => {
                // Match against agent._id since user.agentId is the agent's _id
                return String(agent._id) === String(userAgentId);
            });
            
            if (matchingAgent) {
                // Use the same value format as dropdown options (agent.agentId || agent._id)
                // This ensures the dropdown value matches what we set
                const agentValue = matchingAgent.agentId || matchingAgent._id;
                setFormData(prev => {
                    // Only update if different to avoid unnecessary re-renders
                    if (prev.activeAgentId !== agentValue) {
                        return {
                            ...prev,
                            activeAgentId: agentValue
                        };
                    }
                    return prev;
                });
            }
        }
    }, [agents, userAgentId]);

    const fetchAgents = async () => {
        try {
            setLoadingAgents(true);
            const response = await getAgents({ page: 1, limit: 1000 });
            if (response.success || response.data) {
                const agentsData = Array.isArray(response.data) ? response.data : (response.data?.agents || []);
                setAgents(agentsData);
            }
        } catch (error) {
            console.error('Error fetching agents:', error);
            setError('Failed to load agents. Please refresh the page.');
        } finally {
            setLoadingAgents(false);
        }
    };

    const fetchUserDetails = async () => {
        try {
            setFetching(true);
            const response = await getUserById(id);
            if (response.success || response.data) {
                const user = response.data || response;
                
                // Extract agent ID from user data (user.agentId is the agent's _id)
                const currentAgentId = user.agentId || user.agent?.agentId || user.agent?._id || user.activeAgentId || '';
                setUserAgentId(currentAgentId);
                
                // Preserve existing activeAgentId if already set
                setFormData(prev => {
                    // If agents are already loaded, try to set agent immediately
                    if (agents.length > 0 && currentAgentId && !prev.activeAgentId) {
                        const matchingAgent = agents.find(agent => String(agent._id) === String(currentAgentId));
                        if (matchingAgent) {
                            const agentValue = matchingAgent.agentId || matchingAgent._id;
                            return {
                                name: user.name || '',
                                email: user.email || '',
                                gender: user.gender || '',
                                mobileNumber: user.mobileNumber || '',
                                status: user.status || 'active',
                                activeAgentId: agentValue
                            };
                        }
                    }
                    
                    // Update form data but preserve activeAgentId if already set
                    return {
                        name: user.name || '',
                        email: user.email || '',
                        gender: user.gender || '',
                        mobileNumber: user.mobileNumber || '',
                        status: user.status || 'active',
                        activeAgentId: prev.activeAgentId || '' // Preserve existing value
                    };
                });
            }
        } catch (error) {
            setError(error.message || 'Failed to fetch user details');
            setTimeout(() => navigate('/admin/users'), 2000);
        } finally {
            setFetching(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }
        
        try {
            setLoading(true);
            setError('');
            const payload = {
                ...formData,
                agentId: formData.activeAgentId
            };
            delete payload.activeAgentId;
            
            await updateUser(id, payload);
            alert('User updated successfully');
            navigate('/admin/users');
        } catch (error) {
            setError(error.message || 'Failed to update user');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="container-xxl">
                <PageTitle pagetitle="Edit User" sidebutton={true} />
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted">Loading user details...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Edit User" sidebutton={true} />
            <div className="row">
                <div className="col-xl-8 col-lg-10 mx-auto">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom py-3">
                            <div className="d-flex align-items-center">
                                <i className="fa fa-user-edit text-primary me-2" style={{ fontSize: '20px' }}></i>
                                <Card.Title className="mb-0 fw-bold">Edit User</Card.Title>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4">
                            {error && (
                                <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
                                    <i className="fa fa-exclamation-circle me-2"></i>
                                    {error}
                                </Alert>
                            )}
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Full Name <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter full name"
                                                required
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter full name
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Email <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Enter email address"
                                                required
                                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid email address
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Gender <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleChange}
                                                required
                                                className="form-control-lg"
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="other">Other</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                Please select gender
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Mobile Number
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mobileNumber"
                                                value={formData.mobileNumber}
                                                onChange={handleChange}
                                                placeholder="Enter mobile number"
                                                pattern="[0-9]{10}"
                                                className="form-control-lg"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid 10-digit mobile number
                                            </Form.Control.Feedback>
                                            <Form.Text className="text-muted">
                                                10 digits required
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Status <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                                required
                                                className="form-control-lg"
                                            >
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                                <option value="blocked">Blocked</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                Please select status
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Active Agent ID <span className="text-muted">(Optional)</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="activeAgentId"
                                                value={formData.activeAgentId || ''}
                                                onChange={handleChange}
                                                disabled={loadingAgents}
                                                className="form-control-lg"
                                            >
                                                <option value="">{loadingAgents ? 'Loading agents...' : 'Select Agent'}</option>
                                                {agents.map((agent) => {
                                                    const optionValue = agent.agentId || agent._id;
                                                    return (
                                                        <option key={agent._id} value={optionValue}>
                                                            {agent.agentId || agent._id} {agent.name ? `- ${agent.name}` : ''} {agent.email ? `(${agent.email})` : ''}
                                                        </option>
                                                    );
                                                })}
                                            </Form.Select>
                                            {agents.length === 0 && !loadingAgents && (
                                                <Form.Text className="text-warning">
                                                    <i className="fa fa-exclamation-triangle me-1"></i> No agents available. Please create an agent first.
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/admin/users')}
                                        className="px-4"
                                    >
                                        <i className="fa fa-times me-2"></i> Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={loading}
                                        className="px-4"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-save me-2"></i> Update User
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
            
            <style>{`
                .form-control-lg:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
                }
                .card {
                    transition: transform 0.2s ease-in-out;
                }
                .form-label {
                    color: #495057;
                }
                .text-danger {
                    font-weight: 600;
                }
            `}</style>
        </div>
    );
};

export default EditUser;
